import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import { LocationField } from '@/components/common/custom-fields/location-filed';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import useGetAvatar from '@/hooks/useGetAvatar';
import useGetCompany from '@/hooks/useGetCompany';
import useGetCurrentUserV2 from '@/hooks/useGetCurrentUser';
import useGetMetadataAvatarTypes from '@/hooks/useGetMetadataAvatarTypes';
import { composeUri } from '@/utils/compose';
import {
    ADDRESS_TEXT,
    AVATAR_GROUP_STANDARD_TYPE,
    AVATARS,
    AVATARS_ADD_GROUP_AVATARS,
    BIG_AGENCY_ADMIN,
    BIG_AGENCY_MANAGER,
    BIG_AGENCY_OPERATOR,
    CITY,
    CLIENTS,
    CONTACT_EMAIL_AVATAR_PROPERTY,
    CONTACT_NAME_AVATAR_PROPERTY,
    CONTACT_PHONE_AVATAR_PROPERTY,
    COUNTRY,
    FINGER_ID,
    FINGERS,
    GROUP_AVATAR_SITE_PREFIX,
    GROUP_AVATARS,
    INCIDENT_SERVICE_SITE,
    POSTAL_CODE,
    ROLE_BIG_DEMO,
    SITES,
    SITES_METADATA_AVATAR_TYPE_NAME,
} from '@/utils/CONST';
import extractIdFromURI from '@/utils/id';
import useGetAlphaId from '@/utils/useGetAlphaId';
import { AddHomeOutlined, InfoOutlined } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { composeUriId } from 'base-tools-360smartconnect/utils/compose';
import { isNil, map, omitBy } from 'lodash';
import {
    BooleanInput,
    Form,
    required,
    TextInput,
    useCreate,
    useNotify,
    usePermissions,
    useTranslate,
} from 'react-admin';

interface CreateSiteDialogProps {
    open: boolean;
    onClose: () => void;
    clientId: string;
}

const CreateSiteDialog = ({ open, onClose, clientId }: CreateSiteDialogProps) => {
    const translate = useTranslate();
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const notify = useNotify();
    const { isLoadingGetAlphaId, getAlphaId } = useGetAlphaId({
        service: INCIDENT_SERVICE_SITE,
    });
    const { permissions } = usePermissions();

    const { currentUser } = useGetCurrentUserV2();
    const { company, isLoading: isLoadingCompany } = useGetCompany({ companyUriId: currentUser?.company });

    const { firstMetadataAvatarType: metadataAvatarType, isLoading: isLoadingMetadataAvatarType } =
        useGetMetadataAvatarTypes({
            filters: {
                alphaId: SITES_METADATA_AVATAR_TYPE_NAME,
            },
        });
    const { avatar, isLoading: isLoadingAvatar } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, clientId),
        queryKey: CLIENTS
    });
    const queryClient = useQueryClient();

    const rawPropertiesOnObject = [
        CONTACT_EMAIL_AVATAR_PROPERTY,
        CONTACT_PHONE_AVATAR_PROPERTY,
        CONTACT_NAME_AVATAR_PROPERTY,
    ];

    const isAgencyUser = () => {
        const userTags = currentUser?.tags || [];
        return (
            userTags.includes(BIG_AGENCY_MANAGER) ||
            userTags.includes(BIG_AGENCY_OPERATOR) ||
            userTags.includes(BIG_AGENCY_ADMIN)
        );
    };

    const buildBaseSiteData = (formData: any) => {
        return {
            parent: avatar?.id,
            name: formData.name,
            addressText: formData.addressText,
            postalCode: formData.postalCode.toString(),
            city: formData.city,
            country: formData.country,
            latitude: formData.latitude ? formData.latitude.toString() : null,
            longitude: formData.longitude ? formData.longitude.toString() : null,
            mcs: [],
            metadataAvatarType: metadataAvatarType?.id,
            customFields: {
                demo: !!formData.demo,
            },
            company: currentUser?.company,
        };
    };

    const buildProperties = (avatarProperties: any) => {
        return map(rawPropertiesOnObject, (key) => ({
            name: key,
            value: avatarProperties[key],
            fieldType: 'string',
            private: false,
        }));
    };

    const buildSiteDataV2 = (formData: any, alphaId: string) => {
        return omitBy(
            {
                ...buildBaseSiteData(formData),
                generateMCFinger: composeUriId(FINGERS, FINGER_ID),
                generateMCQuantity: 1,
                properties: buildProperties(formData),
                alphaId,
            },
            isNil,
        );
    };

    const buildSiteDataV1 = (formData: any, alphaId: string) => {
        return omitBy(
            {
                ...buildBaseSiteData(formData),
                generateMC: {
                    finger: composeUri(FINGERS, FINGER_ID),
                    quantity: 1,
                },
                rawProperties: buildProperties(formData),
                alphaId,
            },
            isNil,
        );
    };

    const handleAgencySiteSuccess = () => {
        queryClient.invalidateQueries({
            queryKey: [AVATARS],
        });
        notify('ra.notification.created', { type: 'success' });
        onClose();
    };

    const handleRegularSiteSuccess = (createdSite: any) => {
        const siteId = extractIdFromURI(createdSite.id);
        const groupName = `${GROUP_AVATAR_SITE_PREFIX}${siteId}`;

        create(
            GROUP_AVATARS,
            {
                data: {
                    name: groupName,
                    alphaId: groupName,
                    company: currentUser?.company,
                    type: AVATAR_GROUP_STANDARD_TYPE,
                },
            },
            {
                onSuccess: (avatarGroup: any) => {
                    create(
                        `${SITES}_${clientId}`,
                        {
                            data: {
                                groupAvatar: avatarGroup.id,
                                avatars: [createdSite.id],
                            },
                            meta: {
                                realResource: AVATARS_ADD_GROUP_AVATARS,
                            },
                        },
                        {
                            onSuccess: () => {
                                notify('ra.notification.created', { type: 'success' });
                                onClose();
                            },
                        },
                    );
                },
            },
        );
    };

    const createAgencySite = (formData: any, alphaId: string) => {
        const siteData = buildSiteDataV1(formData, alphaId);

        create(
            `${SITES}_${clientId}`,
            {
                data: {
                    createSite: siteData,
                },
                meta: {
                    realResource: 'big/resource/create-site-as-agency',
                },
            },
            {
                onSuccess: handleAgencySiteSuccess,
            },
        );
    };

    const createRegularSite = (formData: any, alphaId: string) => {
        const siteData = buildSiteDataV2(formData, alphaId);

        create(
            `${SITES}_${clientId}`,
            {
                data: siteData,
                meta: {
                    api: 'v2',
                    realResource: AVATARS,
                },
            },
            {
                onSuccess: handleRegularSiteSuccess,
            },
        );
    };

    const handleSubmit = (formData: any) => {
        getAlphaId().then((data: { counter: number; identifier: string }) => {
            const alphaId = `S:${data.counter}`;
            const isAgency = isAgencyUser();

            if (isAgency) {
                createAgencySite(formData, alphaId);
            } else {
                createRegularSite(formData, alphaId);
            }
        });
    };

    return (
        <DialogTemplate
            open={open}
            onClose={onClose}
            icon={<AddHomeOutlined />}
            title={translate('site.create_site_label', {
                clientName: avatar?.name,
            })}
            closeButtonDisabled={isLoadingCreate}
        >
            <Form
                key={
                    open
                        ? `create-site-${clientId}-${isLoadingCompany ? 'loading' : 'ready'}-${company?.id ?? ''}`
                        : 'closed'
                }
                noValidate
                onSubmit={handleSubmit}
                defaultValues={{
                    latitude: company?.latitude,
                    longitude: company?.longitude,
                    location:
                        company?.latitude && company?.longitude
                            ? `Lat: ${company?.latitude}, Lng: ${company?.longitude}`
                            : undefined,
                    postalCode: company?.postalCode,
                    addressText: company?.additionalAddress,
                    country: company?.country,
                    city: company?.city,
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        <TextInput
                            helperText={false}
                            name={'name'}
                            source={'name'}
                            label={'site.form.site_name'}
                            validate={required()}
                        />

                        <Typography fontSize={12} color={'gray'} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoOutlined />
                            {translate('site.create_site_info')}
                        </Typography>

                        <LocationField readOnly={true} />

                        <TextInput
                            helperText={false}
                            name={ADDRESS_TEXT}
                            source={ADDRESS_TEXT}
                            label={'site.form.addressText'}
                            validate={required()}
                            readOnly={true}
                        />

                        <TextInput
                            helperText={false}
                            name={POSTAL_CODE}
                            source={POSTAL_CODE}
                            label={'site.form.postalCode'}
                            validate={required()}
                            readOnly={true}
                        />

                        <TextInput
                            helperText={false}
                            name={CITY}
                            source={CITY}
                            label={'site.form.city'}
                            validate={required()}
                            readOnly={true}
                        />

                        <TextInput
                            helperText={false}
                            name={COUNTRY}
                            source={COUNTRY}
                            label={'site.form.country'}
                            readOnly={true}
                        />

                        <TextInput
                            helperText={false}
                            name={CONTACT_NAME_AVATAR_PROPERTY}
                            source={CONTACT_NAME_AVATAR_PROPERTY}
                            label={'site.form.name'}
                            validate={required()}
                        />

                        <TextInput
                            helperText={false}
                            name={CONTACT_PHONE_AVATAR_PROPERTY}
                            source={CONTACT_PHONE_AVATAR_PROPERTY}
                            label={'site.form.phone'}
                            validate={required()}
                        />

                        <TextInput
                            helperText={false}
                            name={CONTACT_EMAIL_AVATAR_PROPERTY}
                            source={CONTACT_EMAIL_AVATAR_PROPERTY}
                            label={'site.form.email'}
                            validate={required()}
                        />

                        {permissions?.includes(ROLE_BIG_DEMO) && (
                            <BooleanInput
                                label='site.form.demo'
                                source='demo'
                                helperText={false}
                                sx={{
                                    ml: 1,
                                }}
                            />
                        )}
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button onClick={onClose} disabled={isLoadingCreate}>
                            {translate('ra.action.back')}
                        </Button>

                        <LoadingButton
                            loading={
                                isLoadingCreate || isLoadingMetadataAvatarType || isLoadingAvatar || isLoadingGetAlphaId
                            }
                            type={'submit'}
                            variant={'contained'}
                        >
                            {translate('ra.action.create')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default CreateSiteDialog;
